import React, { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  ChevronDown,
  Circle,
  CircleDashed,
  Clock,
  ExternalLink,
  HelpCircle,
  Loader2,
  MoreHorizontal,
  RotateCcw,
  Sparkles,
  X,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { IntakeWizard } from "@/components/onboarding/IntakeWizard";
import { cn } from "@/lib/utils";
import {
  type ChecklistStatus,
  type SetupChecklistItem,
  useCompleteChecklistItem,
  useResetChecklistItem,
  useSetupChecklist,
  useSkipChecklistItem,
  useSnoozeChecklistItem,
} from "@/hooks/useSetupChecklist";

// =============================================================================
// Setup Center — quiet, narrative, modeled after the Dashboard rewrite.
//
// Layout:
//   1. Hero: progress + one-line orientation
//   2. Critical strip (only renders if there are unresolved high-penalty items)
//   3. "To do" — single card with a clean list of rows, dividers between
//   4. "Done & dismissed" — collapsed by default, low-key
//
// Each row has ONE primary action, with secondary actions tucked in a hover
// overflow menu so the screen isn't a wall of buttons.
// =============================================================================

function splitRoute(raw: string | null | undefined): { path?: string; hash?: string } {
  if (!raw) return {};
  const hashIx = raw.indexOf("#");
  if (hashIx < 0) return { path: raw };
  return { path: raw.slice(0, hashIx) || "/", hash: raw.slice(hashIx + 1) };
}

const isTerminal = (s: ChecklistStatus) => s === "Completed" || s === "Skipped";

export function SetupCenterPage() {
  const checklist = useSetupChecklist();

  // Loading skeleton — avoid flashing the intake form or a hero with
  // zeros while we decide which mode we're in.
  if (checklist.isLoading) {
    return (
      <div className="space-y-8">
        <Card className="border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card">
          <CardContent className="flex items-center gap-3 py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Loading your setup…</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (checklist.isError) {
    return (
      <div className="space-y-8">
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="py-6 text-sm text-destructive">
            Couldn&apos;t load the setup. Make sure the API is running.
          </CardContent>
        </Card>
      </div>
    );
  }

  // Stage gate: the conversational intake wizard runs when the planner
  // still needs answers. AppShell's route guard typically catches this on
  // first load, but users landing here directly (deep-linked /setup) will
  // see the same wizard inline instead of a flat form.
  if (checklist.data?.stage === "Intake") {
    return <IntakeWizard />;
  }

  const ordered = [...(checklist.data?.items ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );
  const progress = checklist.data?.progress;
  const total = progress?.total ?? 0;
  const done = progress?.completed ?? 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  const open = ordered.filter((i) => !isTerminal(i.status));
  const finished = ordered.filter((i) => isTerminal(i.status));
  const urgent = open.filter((i) => i.isHighPenalty);

  return (
    <div className="space-y-8">
      <SetupHero done={done} total={total} pct={pct} loading={false} />

      {urgent.length > 0 && <UrgentStrip items={urgent} />}

      <Section
        eyebrow="To do"
        title={
          open.length === 0
            ? "Foundation locked in. Nothing left here."
            : `${open.length} item${open.length === 1 ? "" : "s"} left`
        }
      >
        {open.length === 0 ? (
          <EmptyState />
        ) : (
          <Card>
            <ul className="divide-y divide-border/60">
              {open.map((item) => (
                <ChecklistRow key={item.key} item={item} />
              ))}
            </ul>
          </Card>
        )}
      </Section>

      {finished.length > 0 && <DoneAccordion items={finished} />}
    </div>
  );
}

// =============================================================================
// HERO
// =============================================================================

function SetupHero({
  done,
  total,
  pct,
  loading,
}: {
  done: number;
  total: number;
  pct: number;
  loading: boolean;
}) {
  return (
    <Card className="overflow-hidden border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card">
      <CardContent className="p-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="min-w-0 max-w-2xl">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              Setup Center
            </div>
            <h1 className="mt-2 text-3xl font-bold tracking-tight">
              Lock in the foundation
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Each row is one decision or one action. Items that auto-track flip
              to done the moment the underlying record exists. The rest take a
              minute and unlock real numbers across the rest of the app.
            </p>
          </div>

          <div className="flex shrink-0 flex-col items-end gap-2">
            <div className="flex items-baseline gap-2">
              {loading ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              ) : (
                <>
                  <div className="text-4xl font-bold tabular-nums tracking-tight">
                    {done}
                  </div>
                  <div className="text-base text-muted-foreground">/ {total} done</div>
                </>
              )}
            </div>
            <div className="h-1.5 w-48 overflow-hidden rounded-full bg-background/40">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
              {pct}% complete
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// URGENT STRIP — only renders if there are unresolved high-penalty items
// =============================================================================

function UrgentStrip({ items }: { items: SetupChecklistItem[] }) {
  return (
    <Card className="border-destructive/40 bg-destructive/5">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-destructive">
              Penalty risk
            </div>
            <h2 className="mt-1 text-base font-semibold">
              {items.length === 1
                ? "1 high-penalty item is still open"
                : `${items.length} high-penalty items are still open`}
            </h2>
            <ul className="mt-3 space-y-1.5">
              {items.map((it) => (
                <li
                  key={it.id}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
                >
                  <span className="font-medium">{it.title}</span>
                  {it.estimatedRiskLabel && (
                    <span className="text-xs text-destructive">
                      · {it.estimatedRiskLabel}
                    </span>
                  )}
                  {it.routeOnClick && (
                    <Link
                      to={splitRoute(it.routeOnClick).path!}
                      hash={splitRoute(it.routeOnClick).hash}
                      className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-destructive hover:underline"
                    >
                      Resolve <ArrowRight className="h-3 w-3" />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// SECTION wrapper
// =============================================================================

function Section({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-4">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
          {eyebrow}
        </div>
        <h2 className="mt-1 text-xl font-semibold tracking-tight">{title}</h2>
      </div>
      {children}
    </section>
  );
}

// =============================================================================
// ROW — one checklist item rendered as a clean horizontal row
// =============================================================================

function ChecklistRow({ item }: { item: SetupChecklistItem }) {
  const complete = useCompleteChecklistItem();
  const skip = useSkipChecklistItem();
  const reset = useResetChecklistItem();
  const snooze = useSnoozeChecklistItem();
  const [menuOpen, setMenuOpen] = useState(false);

  const { path, hash } = splitRoute(item.routeOnClick);
  const showOpen = !!path;
  const showMarkDone = !item.isAutoTracked;

  return (
    <li className="group relative">
      <div className="flex items-start gap-4 px-5 py-4 transition-colors hover:bg-muted/20">
        <StatusIcon status={item.status} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <h3 className="text-sm font-semibold leading-tight">{item.title}</h3>
            {item.isAutoTracked && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
                title="Auto-tracked: flips to done the moment the underlying record exists"
              >
                <Sparkles className="h-3 w-3" />
                auto
              </span>
            )}
            {item.isHighPenalty && (
              <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-red-300">
                penalty
              </span>
            )}
            {item.status === "Snoozed" && (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                <Clock className="h-3 w-3" />
                snoozed
              </span>
            )}
          </div>
          {item.whyItMatters && (
            <p className="mt-1 text-sm leading-snug text-muted-foreground">
              {item.whyItMatters}
            </p>
          )}
          {item.shownBecause && (
            <p className="mt-1.5 flex items-start gap-1.5 text-[11px] leading-snug text-primary/80">
              <HelpCircle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>
                <span className="font-medium uppercase tracking-wider">
                  Shown because:
                </span>{" "}
                {item.shownBecause}
              </span>
            </p>
          )}
          {(item.estimatedRiskLabel || item.authorityUrl) && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              {item.estimatedRiskLabel && (
                <span
                  className={cn(
                    "tabular-nums",
                    item.isHighPenalty ? "text-red-300" : "text-amber-300",
                  )}
                >
                  {item.estimatedRiskLabel}
                </span>
              )}
              {item.authorityUrl && (
                <a
                  href={item.authorityUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 hover:text-foreground"
                >
                  Reference <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {showOpen && (
            <Link to={path!} hash={hash}>
              <Button size="sm" variant={item.isHighPenalty ? "primary" : "outline"}>
                {item.routeLabel ?? "Open"}
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          )}

          {/* Overflow menu: secondary actions tucked away */}
          <div className="relative">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="More actions"
              aria-expanded={menuOpen}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
            {menuOpen && (
              <>
                <button
                  type="button"
                  className="fixed inset-0 z-10 cursor-default"
                  aria-hidden
                  onClick={() => setMenuOpen(false)}
                />
                <div className="absolute right-0 top-9 z-20 w-48 rounded-md border border-border bg-card p-1 shadow-lg">
                  {showMarkDone && (
                    <MenuItem
                      icon={<Check className="h-3.5 w-3.5" />}
                      label="Mark done"
                      disabled={complete.isPending}
                      onClick={() => {
                        complete.mutate(item.key);
                        setMenuOpen(false);
                      }}
                    />
                  )}
                  {!item.isAutoTracked && item.status !== "Snoozed" && (
                    <MenuItem
                      icon={<Clock className="h-3.5 w-3.5" />}
                      label="Snooze 7 days"
                      disabled={snooze.isPending}
                      onClick={() => {
                        snooze.mutate({ key: item.key });
                        setMenuOpen(false);
                      }}
                    />
                  )}
                  <MenuItem
                    icon={<X className="h-3.5 w-3.5" />}
                    label="Doesn't apply"
                    disabled={skip.isPending}
                    onClick={() => {
                      skip.mutate(item.key);
                      setMenuOpen(false);
                    }}
                  />
                  {item.status !== "NotStarted" && (
                    <MenuItem
                      icon={<RotateCcw className="h-3.5 w-3.5" />}
                      label="Reset"
                      disabled={reset.isPending}
                      onClick={() => {
                        reset.mutate(item.key);
                        setMenuOpen(false);
                      }}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs text-foreground hover:bg-muted/60 disabled:pointer-events-none disabled:opacity-50"
    >
      <span className="text-muted-foreground">{icon}</span>
      {label}
    </button>
  );
}

// =============================================================================
// STATUS ICON — small, monochrome-ish, no shouty fills
// =============================================================================

function StatusIcon({ status }: { status: ChecklistStatus }) {
  const cls = "mt-0.5 h-5 w-5 shrink-0";
  switch (status) {
    case "Completed":
      return (
        <span className={cn(cls, "rounded-full bg-emerald-500/20 p-0.5")}>
          <Check className="h-4 w-4 text-emerald-300" />
        </span>
      );
    case "Skipped":
      return <X className={cn(cls, "text-muted-foreground")} />;
    case "Snoozed":
      return <Clock className={cn(cls, "text-muted-foreground")} />;
    case "InProgress":
      return <CircleDashed className={cn(cls, "text-primary")} />;
    default:
      return <Circle className={cn(cls, "text-muted-foreground")} />;
  }
}

// =============================================================================
// DONE ACCORDION
// =============================================================================

function DoneAccordion({ items }: { items: SetupChecklistItem[] }) {
  const [open, setOpen] = useState(false);
  const completed = items.filter((i) => i.status === "Completed").length;
  const skipped = items.length - completed;

  return (
    <section>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="group flex w-full items-center gap-2 text-left"
      >
        <ChevronDown
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground group-hover:text-foreground">
          Done & dismissed
        </span>
        <span className="text-xs text-muted-foreground">
          ({completed} done{skipped > 0 ? `, ${skipped} skipped` : ""})
        </span>
      </button>
      {open && (
        <Card className="mt-3 opacity-80">
          <ul className="divide-y divide-border/60">
            {items.map((item) => (
              <ChecklistRow key={item.id} item={item} />
            ))}
          </ul>
        </Card>
      )}
    </section>
  );
}

// =============================================================================
// EMPTY STATE
// =============================================================================

function EmptyState() {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <span className="rounded-full bg-emerald-500/20 p-2">
          <Check className="h-5 w-5 text-emerald-300" />
        </span>
        <p className="text-sm text-muted-foreground">
          Foundation is locked in. Anything new will land here automatically.
        </p>
      </CardContent>
    </Card>
  );
}

// Keep the type exported for the hook callers.
export type { SetupChecklistItem };
